import * as React from 'react';
import { loadCheckoutWebComponents, ComponentName } from '@checkout.com/checkout-web-components';
import type { CheckoutWebComponents, Component, PayPaymentSessionSuccessfulResponse } from '@checkout.com/checkout-web-components';

export type WalletType = 'apple_pay' | 'google_pay';
export type CheckoutMethodType = 'payment_card' | WalletType;


const SANS_SERIF_FALLBACK =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif';

const FLOW_APPEARANCE = {
    colorBackground: '#FFFFFF',
    colorFormBackground: '#FFFFFF',
    colorBorder: '#E3E3E3',
    colorFormBorder: '#E3E3E3',
    colorPrimary: '#000000',
    colorSecondary: '#747474',
    colorAction: '#000000',
    colorInverse: '#FFFFFF',
    colorOutline: '#000000',
    colorError: '#FF0060',
    colorSuccess: '#1BCB42',
    colorDisabled: '#ADADAD',
    borderRadius: ['16px', '8px'] as [string, string],
    label: {
        fontFamily: `Ambit-Regular, ${SANS_SERIF_FALLBACK}`,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '16px',
    },
    input: {
        fontFamily: `Ambit-Regular, ${SANS_SERIF_FALLBACK}`,
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
    },
    subheading: {
        fontFamily: `Ambit-Bold, ${SANS_SERIF_FALLBACK}`,
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
    },
    button: {
        fontFamily: `Ambit-SemiBold, ${SANS_SERIF_FALLBACK}`,
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '24px',
    },
    footnote: {
        fontFamily: `Ambit-Regular, ${SANS_SERIF_FALLBACK}`,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '16px',
    },
};

function getCheckoutComponentName(checkoutMethodType: CheckoutMethodType | null): ComponentName {
    switch (checkoutMethodType) {
        case 'payment_card': return ComponentName.Card;
        case 'apple_pay': return ComponentName.Applepay;
        case 'google_pay': return ComponentName.Googlepay;
        default: return ComponentName.Flow;
    }
}


export function getCkoPublicKey(): string {
    const isSandbox = window.location.hostname === 'localhost' || window.location.hostname.includes('dev.');
    const publicKey = isSandbox
        ? process.env.REACT_APP_CHECKOUT_DOT_COM_PUBLIC_KEY_DEV
        : process.env.REACT_APP_CHECKOUT_DOT_COM_PUBLIC_KEY_PROD;
    if (!publicKey) throw new Error(`Missing CKO public key (${isSandbox ? 'sandbox' : 'production'})`);
    return publicKey;
}

export function isCkoSandbox(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname.includes('dev.');
}

export function decodePaymentSessionToken(token: string): Record<string, unknown> {
    const colonIndex = token.indexOf(':');
    const base64Data = colonIndex !== -1 ? token.substring(colonIndex + 1) : token;
    const decoded = JSON.parse(atob(base64Data));
    return {
        id: decoded.id,
        payment_session_token: token,
        payment_session_secret: decoded.payment_session_secret,
    };
}

export function useLoadCheckoutFlowComponent(
    paymentSessionToken: string | null,
    setStatus: (status: 'loading' | 'ready' | 'error') => void,
    successUrl: string,
    failureUrl: string,
    checkoutMethodType: CheckoutMethodType | null,
    checkoutComponentRef: React.MutableRefObject<Component | null>,
    onWalletAvailabilityProbed: (methods: Set<WalletType>) => void,
) {
    // Always holds the latest successUrl so onPaymentCompleted doesn't close over a stale value
    const successUrlRef = React.useRef(successUrl);
    successUrlRef.current = successUrl;

    // Cached across wallet switches — re-created only when paymentSessionToken changes
    const checkoutInstanceRef = React.useRef<CheckoutWebComponents | null>(null);
    const prevTokenRef = React.useRef<string | null>(null);
    // Availability is probed once per payment session, immediately after instance creation
    const availabilityCheckedRef = React.useRef(false);

    React.useEffect(() => {
        if (!paymentSessionToken) {
            setStatus('error');
            return;
        }
        // Reset cached checkout instance when payment session changes
        if (paymentSessionToken !== prevTokenRef.current) {
            checkoutInstanceRef.current = null;
            availabilityCheckedRef.current = false;
            prevTokenRef.current = paymentSessionToken;
        }
        let isMounted = true;
        // Captured locally so cleanup can unmount even if the ref was cleared externally
        let mountedComponent: Component | null = null;

        function mountComponent(component: Component, container: HTMLElement) {
            component.mount(container);
            mountedComponent = component;
            checkoutComponentRef.current = component;
            setStatus('ready');
        }

        async function initializeFlow() {
            try {
                const publicKey = getCkoPublicKey();
                const isSandbox = isCkoSandbox();

                // Initialize checkout only once per payment session
                if (!checkoutInstanceRef.current) {
                    const checkout = await loadCheckoutWebComponents({
                        paymentSession: decodePaymentSessionToken(paymentSessionToken!),
                        publicKey,
                        environment: isSandbox ? 'sandbox' : 'production',
                        appearance: FLOW_APPEARANCE,
                        onPaymentCompleted: (_component, paymentResponse: PayPaymentSessionSuccessfulResponse) => {
                            const paymentId = paymentResponse.id;
                            const url = successUrlRef.current;
                            const separator = url.includes('?') ? '&' : '?';
                            const redirectUrl = paymentId
                                ? `${url}${separator}cko-payment-id=${encodeURIComponent(paymentId)}`
                                : url;
                            window.location.href = redirectUrl;
                        },
                    });
                    if (!isMounted) return;
                    checkoutInstanceRef.current = checkout;
                }

                // Probe wallet availability once per session; result drives the picker UI
                if (!availabilityCheckedRef.current) {
                    availabilityCheckedRef.current = true;
                    const available = new Set<WalletType>();
                    try {
                        if (await checkoutInstanceRef.current.create(ComponentName.Applepay).isAvailable()) available.add('apple_pay');
                    } catch {}
                    try {
                        if (await checkoutInstanceRef.current.create(ComponentName.Googlepay).isAvailable()) available.add('google_pay');
                    } catch {}
                    if (isMounted) onWalletAvailabilityProbed(available);
                }

                // Wait for the caller to select a method before mounting
                if (!checkoutMethodType) return;

                // Card form mounts into its own box (above checkbox); wallet button mounts into pay button area (below checkbox)
                const containerId = checkoutMethodType === 'payment_card' ? 'checkout-card-container' : 'checkout-flow-container';
                activeContainerId = containerId;
                const container = document.getElementById(containerId);
                if (!container) throw new Error(`Flow container not found: ${containerId}`);

                const componentName = getCheckoutComponentName(checkoutMethodType);
                const component = checkoutInstanceRef.current.create(
                    componentName,
                    componentName === ComponentName.Card ? { showPayButton: false } : undefined
                );
                await component.isAvailable();
                if (!isMounted) return;
                mountComponent(component, container);
            } catch (error) {
                if (!isMounted) return;
                console.error('Error initializing checkout.com Flow:', error);
                setStatus('error');
            }
        }
        let activeContainerId: string | null = null;
        initializeFlow();
        return () => {
            isMounted = false;
            mountedComponent?.unmount();
            checkoutComponentRef.current = null;
            const container = activeContainerId ? document.getElementById(activeContainerId) : null;
            if (container) {
                while (container.firstChild) container.removeChild(container.firstChild);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentSessionToken, setStatus, successUrl, failureUrl, checkoutMethodType, checkoutComponentRef, onWalletAvailabilityProbed]);
}
